// /login — class code + display name fallback.
//
// Used when a kid's personal short-URL sticker is lost or they switch device.
// Class code is normalized to uppercase as the kid types so case never matters.

import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLoginByCode } from '../auth/hooks.ts'

export function LoginPage() {
  const navigate = useNavigate()
  const { mutate, isPending, error } = useLoginByCode()
  const [classCode, setClassCode] = useState('')
  const [displayName, setDisplayName] = useState('')

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    mutate(
      { classCode: classCode.trim().toUpperCase(), displayName: displayName.trim() },
      { onSuccess: () => navigate('/', { replace: true }) },
    )
  }

  const ready = classCode.trim().length === 6 && displayName.trim().length > 0

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-6 p-8">
      <header className="text-center">
        <h1 className="font-[family-name:var(--font-display)] text-3xl text-[var(--color-joseph-primary)]">
          Skena
        </h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">用班級代碼登入</p>
      </header>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-[var(--color-muted)]">班級代碼（6 字）</span>
          <input
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={6}
            value={classCode}
            onChange={(e) => setClassCode(e.target.value.toUpperCase())}
            className="rounded-[var(--radius-soft)] border border-[var(--color-muted)]/30 bg-white px-4 py-3 text-2xl tracking-[0.4em] uppercase focus:border-[var(--color-joseph-primary)] focus:outline-none"
            placeholder="TBOL01"
            aria-label="class code"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-[var(--color-muted)]">你的名字</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="rounded-[var(--radius-soft)] border border-[var(--color-muted)]/30 bg-white px-4 py-3 text-lg focus:border-[var(--color-joseph-primary)] focus:outline-none"
            placeholder="Alice"
            aria-label="display name"
          />
        </label>

        {error && (
          <p className="text-sm text-amber-700" role="alert">
            {error.code === 'NOT_FOUND' ? '找不到這個名字，請再確認一下' : error.message}
          </p>
        )}

        <button
          type="submit"
          disabled={!ready || isPending}
          className="rounded-[var(--radius-pill)] bg-[var(--color-joseph-primary)] px-6 py-3 text-white disabled:opacity-50"
        >
          {isPending ? '登入中…' : '進入 Skena'}
        </button>
      </form>
    </main>
  )
}
